import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "../context/LanguageContext";
import AuthButton from "../components/AuthButton";
import { finishOnboarding } from "../services/UserService";
import { ChevronRight } from "lucide-react";
import {
  LanguageStep,
  BuildPlanStep,
  StoresSelector,
  isZipCode,
} from "../components/setup";
import { getRootRetailer } from "../utils/billingConfig";
import { getDuplicateRetailers } from "../utils/subscriptionGuards";
import { useBillingContext } from "../context/BillingContext";
import { useUserContext } from "../context/UserContext";
import { checkPastDueSubscription, getProductLink } from "../services/BillingService";
import { track } from "../utils/analytics";
import { reportOnboardingStep } from "../services/LifecycleService";
import { useCategories, useHomeDepotCategories } from "../hooks/useBotFilters";

export default function SetUpPage() {
  const { t } = useTranslation();
  const { user } = useUserContext();

  const [isReinstating, setIsReinstating] = useState(false);
  const [isCheckingReinstatement, setIsCheckingReinstatement] = useState(true);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [zipCode, setZipCode] = useState<string>(user?.zipCode || "");
  const [selectedRetailers, setSelectedRetailers] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(
    user?.categories || []
  );
  const [selectedStoreIds, setSelectedStoreIds] = useState<string[]>(
    user?.storeIds || []
  );
  const [selectedHomeDepotCategories, setSelectedHomeDepotCategories] =
    useState<string[]>(user?.homeDepotCategories || []);
  const [selectedHomeDepotStoreIds, setSelectedHomeDepotStoreIds] = useState<
    string[]
  >(user?.homeDepotStoreIds || []);
  const [error, setError] = useState<string>("");
  const [isLoadingProductLink, setIsLoadingProductLink] = useState(false);
  const [debugHolidaySale, setDebugHolidaySale] = useState<boolean | null>(null); // null = use real eligibility

  const {
    billingConfig,
    productsData,
    isLoading: isBillingLoading,
    getStoreLimit,
  } = useBillingContext();

  // Fetch all categories for automatic selection
  const { data: walmartCategories = [] } = useCategories();
  const { data: homeDepotCategories = [] } = useHomeDepotCategories();

  // Check if user needs to reinstate on mount
  useEffect(() => {
    const checkReinstatementStatus = async () => {
      try {
        const data = await checkPastDueSubscription();
        if (data.needsReinstatement) {
          setIsReinstating(true);
        }
      } catch (error) {
        console.error("Failed to check reinstatement status:", error);
      } finally {
        setIsCheckingReinstatement(false);
      }
    };

    checkReinstatementStatus();
  }, []);

  const hasWalmart = useMemo(() => {
    return selectedRetailers.find((retailer) => retailer.includes("walmart"));
  }, [selectedRetailers, billingConfig]);
  const hasHomeDepot = useMemo(() => {
    return selectedRetailers.find((retailer) =>
      retailer.includes("home_depot")
    );
  }, [selectedRetailers, billingConfig]);

  // Automatically select all categories when retailers are selected and categories are loaded
  useEffect(() => {
    if (
      hasWalmart &&
      walmartCategories.length > 0 &&
      selectedCategories.length === 0
    ) {
      const allWalmartCategoryKeys = walmartCategories.map(
        (cat) => cat.rootKey
      );
      setSelectedCategories(allWalmartCategoryKeys);
    }
    if (
      hasHomeDepot &&
      homeDepotCategories.length > 0 &&
      selectedHomeDepotCategories.length === 0
    ) {
      const allHomeDepotCategoryKeys = homeDepotCategories.map(
        (cat) => cat.rootKey
      );
      setSelectedHomeDepotCategories(allHomeDepotCategoryKeys);
    }
  }, [
    hasWalmart,
    hasHomeDepot,
    walmartCategories,
    homeDepotCategories,
    selectedCategories.length,
    selectedHomeDepotCategories.length,
  ]);

  // Check if user is eligible for holiday sale (50% off first month)
  // Created > Dec 3, 2025 and Current Date < Jan 31, 2026
  const isHolidaySaleEligible = useMemo(() => {
    // Admin debug override
    if (debugHolidaySale !== null) return debugHolidaySale;
    
    if (!user?.created) return false;

    const userCreated = new Date(user.created);
    const cutoffStart = new Date("2025-12-03T00:00:00Z");
    const cutoffEnd = new Date("2026-01-31T00:00:00Z");
    const now = new Date();

    return now < cutoffEnd && userCreated > cutoffStart;
  }, [user?.created, debugHolidaySale]);

  const maxStep = useMemo(() => {
    let maxStep = 2; // Default: Language -> Build Plan
    if (selectedRetailers.length > 0) {
      if (hasWalmart && hasHomeDepot) {
        maxStep = 4; // Language -> Build Plan -> Walmart Stores -> Home Depot Stores
      } else if (hasWalmart) {
        maxStep = 3; // Language -> Build Plan -> Walmart Stores
      } else if (hasHomeDepot) {
        maxStep = 3; // Language -> Build Plan -> Home Depot Stores
      }
    }
    return maxStep;
  }, [hasWalmart, hasHomeDepot, selectedRetailers]);

  // Track step views
  useEffect(() => {
    if (!isCheckingReinstatement) {
      track("setup_step_viewed", {
        step: currentStep,
        step_name: getStepName(currentStep),
        is_reinstating: isReinstating,
        has_walmart: hasWalmart,
        has_home_depot: hasHomeDepot,
      });
      // Legacy flow order: 1 ZIP, 2 plan, 3+ store pickers. Completion is
      // reported from /success once the purchase lands.
      reportOnboardingStep(currentStep === 1 ? "zipcode" : currentStep === 2 ? "paywall" : "stores");
    }
  }, [
    currentStep,
    isCheckingReinstatement,
    isReinstating,
    hasWalmart,
    hasHomeDepot,
  ]);

  const isLastStep = currentStep === maxStep;

  const handleRetailerSelect = (retailer: string, _planId?: string) => {
    // make sure to toggle retailers in same root.
    // _planId parameter is provided by BuildPlanStep but not used in current logic

    const copy = [...selectedRetailers];

    const attemptedRoot = getRootRetailer(retailer);

    for (const selectedRetailer of selectedRetailers) {
      const root = getRootRetailer(selectedRetailer);
      if (root === attemptedRoot && selectedRetailer !== retailer) {
        copy.splice(copy.indexOf(selectedRetailer), 1);
      }
    }

    const idxOfRetailer = copy.indexOf(retailer);
    if (idxOfRetailer > -1) {
      copy.splice(idxOfRetailer, 1);
    } else {
      copy.push(retailer);
    }

    setSelectedRetailers(copy);
  };

  const handleStoreSelect = (
    storeId: string,
    setStoreFn: React.Dispatch<React.SetStateAction<string[]>>,
    stores: string[]
  ) => {
    const indexOfStore = stores.indexOf(storeId);
    const copy = [...stores];

    if (indexOfStore > -1) {
      copy.splice(indexOfStore, 1);
    } else {
      copy.push(storeId);
    }

    setStoreFn(copy);
  };

  // Helper function to get step name for tracking
  const getStepName = (step: number): string => {
    switch (step) {
      case 1:
        return "language-zipcode";
      case 2:
        return "build-plan";
      case 3:
        if (hasWalmart && hasHomeDepot) return "walmart-stores";
        if (hasWalmart) return "walmart-stores";
        if (hasHomeDepot) return "homedepot-stores";
        return "stores";
      case 4:
        return "homedepot-stores";
      default:
        return `step-${step}`;
    }
  };

  function validateStepRequirements(): boolean {
    switch (currentStep) {
      case 3:
        if (hasWalmart && hasHomeDepot) {
          // Walmart stores step (when both retailers selected)
          if (selectedStoreIds.length === 0) {
            const msg =
              "Please select at least one Walmart store before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return false;
          }
        } else if (hasWalmart) {
          // Walmart stores step (Walmart only)
          if (selectedStoreIds.length === 0) {
            const msg =
              "Please select at least one Walmart store before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return false;
          }
        } else if (hasHomeDepot) {
          // Home Depot stores step (Home Depot only)
          if (selectedHomeDepotStoreIds.length === 0) {
            const msg =
              "Please select at least one HD store before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return false;
          }
        }
        break;
      case 4:
        // Home Depot stores step (when both retailers selected)
        if (hasHomeDepot && selectedHomeDepotStoreIds.length === 0) {
          const msg =
            "Please select at least one HD store before continuing.";
          setError(msg);
          track("setup_step_error", {
            step: currentStep,
            step_name: getStepName(currentStep),
            error: msg,
          });
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  }

  const handleContinue = async () => {
        setError("");

        if (currentStep < maxStep) {
          // Validate step 1 - zip code
          if (currentStep === 1) {
            if (!isZipCode(zipCode)) {
              setError(t("setup.invalidZipCodeError"));
              return;
            }
          }

          if (currentStep === 2 && selectedRetailers.length === 0) {
            const msg =
              "Please select at least one retailer before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return;
          }

          // Validate step requirements before proceeding
          if (validateStepRequirements()) {
            // Clear previous selections and auto-select all categories when transitioning from step 2 (plan selection)
            if (currentStep === 2) {
              setSelectedStoreIds([]);
              setSelectedHomeDepotStoreIds([]);

              // Automatically select all categories for selected retailers
              if (hasWalmart && walmartCategories.length > 0) {
                const allWalmartCategoryKeys = walmartCategories.map(
                  (cat) => cat.rootKey
                );
                setSelectedCategories(allWalmartCategoryKeys);
              } else {
                setSelectedCategories([]);
              }

              if (hasHomeDepot && homeDepotCategories.length > 0) {
                const allHomeDepotCategoryKeys = homeDepotCategories.map(
                  (cat) => cat.rootKey
                );
                setSelectedHomeDepotCategories(allHomeDepotCategoryKeys);
              } else {
                setSelectedHomeDepotCategories([]);
              }
            }

            const nextStep = currentStep + 1;
            setCurrentStep(nextStep);

            track("setup_step_completed", {
              from_step: currentStep,
              to_step: nextStep,
              step_name: getStepName(currentStep),
              next_step_name: getStepName(nextStep),
              retailers: selectedRetailers.length > 0 ? selectedRetailers.join(",") : undefined,
              has_walmart: hasWalmart,
              has_home_depot: hasHomeDepot,
              is_reinstating: isReinstating,
            });
          }
        } else {
          // Final step - validate and complete onboarding

          // Validate stores are selected
          if (hasWalmart && selectedStoreIds.length === 0) {
            const msg =
              "Please select at least one Walmart store before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return;
          }
          if (hasHomeDepot && selectedHomeDepotStoreIds.length === 0) {
            const msg =
              "Please select at least one HD store before continuing.";
            setError(msg);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: msg,
            });
            return;
          }

          try {
            // Ensure all categories are set before finishing onboarding
            let finalCategories = selectedCategories;
            let finalHomeDepotCategories = selectedHomeDepotCategories;

            if (hasWalmart && walmartCategories.length > 0) {
              if (selectedCategories.length === 0) {
                finalCategories = walmartCategories.map((cat) => cat.rootKey);
                setSelectedCategories(finalCategories);
              } else {
                finalCategories = selectedCategories;
              }
            }
            if (hasHomeDepot && homeDepotCategories.length > 0) {
              if (selectedHomeDepotCategories.length === 0) {
                finalHomeDepotCategories = homeDepotCategories.map(
                  (cat) => cat.rootKey
                );
                setSelectedHomeDepotCategories(finalHomeDepotCategories);
              } else {
                finalHomeDepotCategories = selectedHomeDepotCategories;
              }
            }

            // Collect all price IDs based on selected retailers and plans
            if (!billingConfig || !productsData) {
              setError(
                "Pricing information not loaded. Please refresh the page."
              );
              // setIsLoading(false);
              return;
            }

            // Get Discord price ID from products data
            const discordPriceId = productsData.discordAccess?.prices?.[0]?.id;
            if (!discordPriceId) {
              setError("Discord pricing information not available. Please refresh the page.");
              return;
            }

            const allPriceIds: string[] = [discordPriceId];

            const rootRetailers: { [key: string]: boolean } = {};

            for (const selectedRetailer of selectedRetailers) {
              const root = selectedRetailer.split("-")[0];
              if (!rootRetailers[root]) {
                rootRetailers[root] = true;
              } else {
                setError(
                  "You can only select one plan per retailer. Please deselect one of the plans and try again."
                );
                return;
              }
              const retailer = billingConfig[selectedRetailer];
              if (retailer) {
                const foundRetailer = retailer.find(
                  (r) => r.retailer === selectedRetailer
                );
                if (foundRetailer) {
                  allPriceIds.push(foundRetailer.priceId);
                }
              }
            }

            // Finish onboarding with setup data only
            await finishOnboarding(
              finalCategories,
              selectedStoreIds,
              finalHomeDepotCategories,
              selectedHomeDepotStoreIds
            );

            if (hasWalmart || hasHomeDepot) {
              // Get Stripe product link and redirect
              track("setup_completed", {
                step: currentStep,
                step_name: getStepName(currentStep),
                retailers: selectedRetailers.join(","),
                has_walmart: hasWalmart,
                has_home_depot: hasHomeDepot,
                walmart_stores: selectedStoreIds.length,
                hd_stores: selectedHomeDepotStoreIds.length,
                is_reinstating: isReinstating,
              });

              // Get Stripe product link and redirect
              setIsLoadingProductLink(true);
              try {
                const duplicateRetailers = getDuplicateRetailers(
                  user,
                  selectedRetailers
                );
                if (!isReinstating && duplicateRetailers.length > 0) {
                  setError(
                    `You already have a plan for ${duplicateRetailers.join(
                      ", "
                    )}. Please manage billing to upgrade or cancel your existing plan.`
                  );
                  setIsLoadingProductLink(false);
                  return;
                }
                const productLinkResponse = await getProductLink(allPriceIds);
                if (productLinkResponse?.link) {
                  window.location.href = productLinkResponse.link;
                } else {
                  throw new Error("No product link received from server");
                }
              } catch (linkError) {
                console.error("Failed to get product link:", linkError);
                setError("Failed to initialize payment. Please try again.");
                track("setup_step_error", {
                  step: currentStep,
                  step_name: getStepName(currentStep),
                  error: "Failed to get product link",
                });
                setIsLoadingProductLink(false);
              }
            } else {
              track("setup_completed_no_retailers", {
                step: currentStep,
                step_name: getStepName(currentStep),
              });
              window.location.href = "/success?retailer_added=true";
            }
          } catch (error) {
            console.error(
              "Error finishing onboarding or getting product link",
              error
            );
            const errorMsg =
              error instanceof Error ? error.message : String(error);
            track("setup_step_error", {
              step: currentStep,
              step_name: getStepName(currentStep),
              error: errorMsg,
            });
            setError(t("setup.errorSetup"));
            // setIsLoading(false);
          }
        }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      const previousStep = currentStep - 1;
      setCurrentStep(previousStep);
      setError("");

      track("setup_step_back", {
        from_step: currentStep,
        to_step: previousStep,
        step_name: getStepName(currentStep),
        previous_step_name: getStepName(previousStep),
      });
    }
  };

  const renderStepContent = () => {
    // Show loading while checking reinstatement status
    if (isCheckingReinstatement) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-dark-300">Loading...</p>
          </div>
        </div>
      );
    }

    switch (currentStep) {
      case 1:
        return <LanguageStep zipCode={zipCode} onZipCodeChange={setZipCode} />;

      case 2:
        if (isBillingLoading) {
          return (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-dark-300">Loading pricing information...</p>
              </div>
            </div>
          );
        }

        if (!billingConfig) {
          return (
            <div className="flex items-center justify-center min-h-[400px]">
              <div className="text-center">
                <p className="text-red-600 dark:text-red-400 mb-4">
                  Failed to load pricing information
                </p>
                <button
                  onPointerDown={() => window.location.reload()}
                  className="px-4 py-2 bg-primary-600 text-white rounded hover:bg-primary-700"
                >
                  Retry
                </button>
              </div>
            </div>
          );
        }

        return (
          <BuildPlanStep
            selectedRetailers={selectedRetailers}
            onPlanSelect={handleRetailerSelect}
            onContinue={handleContinue}
            onBack={handleBack}
            error={error}
            setError={setError}
            billingConfig={billingConfig}
            isHolidaySaleEligible={isHolidaySaleEligible}
          />
        );

      case 3:
        if (hasWalmart && hasHomeDepot) {
          // Walmart stores (when both retailers selected)
          return (
            <StoresSelector
              selectedStoreIds={selectedStoreIds}
              onStoreSelect={(storeId) =>
                handleStoreSelect(
                  storeId,
                  setSelectedStoreIds,
                  selectedStoreIds
                )
              }
              maxStores={getStoreLimit("walmart", selectedRetailers)}
              retailer="walmart"
              onContinue={handleContinue}
              error={error}
              setError={setError}
              zipCode={zipCode}
            />
          );
        } else if (hasWalmart) {
          // Walmart stores (Walmart only)
          return (
            <StoresSelector
              selectedStoreIds={selectedStoreIds}
              onStoreSelect={(storeId) =>
                handleStoreSelect(
                  storeId,
                  setSelectedStoreIds,
                  selectedStoreIds
                )
              }
              maxStores={getStoreLimit("walmart", selectedRetailers)}
              retailer="walmart"
              onContinue={handleContinue}
              error={error}
              setError={setError}
              zipCode={zipCode}
            />
          );
        } else if (hasHomeDepot) {
          // Home Depot stores (Home Depot only)
          return (
            <StoresSelector
              selectedStoreIds={selectedHomeDepotStoreIds}
              onStoreSelect={(storeId) =>
                handleStoreSelect(
                  storeId,
                  setSelectedHomeDepotStoreIds,
                  selectedHomeDepotStoreIds
                )
              }
              maxStores={getStoreLimit("home_depot", selectedRetailers)}
              retailer="home_depot"
              onContinue={handleContinue}
              error={error}
              setError={setError}
              zipCode={zipCode}
            />
          );
        }
        return null;

      case 4:
        if (hasWalmart && hasHomeDepot) {
          // Home Depot stores (when both retailers selected)
          return (
            <StoresSelector
              selectedStoreIds={selectedHomeDepotStoreIds}
              onStoreSelect={(storeId) =>
                handleStoreSelect(
                  storeId,
                  setSelectedHomeDepotStoreIds,
                  selectedHomeDepotStoreIds
                )
              }
              maxStores={getStoreLimit("home_depot", selectedRetailers)}
              retailer="home_depot"
              onContinue={handleContinue}
              error={error}
              setError={setError}
              zipCode={zipCode}
            />
          );
        }
        return null;

      default:
        return null;
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-dark-950 grid grid-rows-[1fr_auto]">
      {/* Content */}
      <div className="overflow-y-auto min-h-0">
        <div
          className={`flex p-4 ${
            currentStep >= 3 ? "items-start pt-4" : "items-center min-h-full"
          } justify-center ${currentStep === 2 ? "pb-4" : "pb-4"}`}
        >
          <div className="w-full max-w-4xl">
            {/* Reinstatement Notice */}
            {isReinstating && currentStep === 1 && (
              <div className="mb-4 p-4 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-amber-600 dark:text-amber-400 mt-0.5"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth="1.5"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
                      />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-300 mb-1">
                      Your Account is Not Active
                    </h3>
                    <p className="text-sm text-amber-800 dark:text-amber-400">
                      Your subscription has been cancelled. Please complete the
                      setup process again to reinstate your account and continue
                      enjoying DealHawk Pro.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Admin Debug Toggle for Holiday Sale */}
            {user?.admin && (
              <div className="mb-4 p-2 bg-gray-800 dark:bg-dark-800 rounded-lg flex items-center justify-between">
                <span className="text-xs text-gray-300 dark:text-dark-300 font-mono">🔧 Debug: Holiday Sale</span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setDebugHolidaySale(debugHolidaySale === true ? null : true)}
                    className={`px-2 py-1 text-[10px] font-bold rounded ${
                      debugHolidaySale === true
                        ? "bg-green-500 text-white"
                        : "bg-gray-600 dark:bg-dark-600 text-gray-300 dark:text-dark-300"
                    }`}
                  >
                    ON
                  </button>
                  <button
                    onClick={() => setDebugHolidaySale(debugHolidaySale === false ? null : false)}
                    className={`px-2 py-1 text-[10px] font-bold rounded ${
                      debugHolidaySale === false
                        ? "bg-red-500 text-white"
                        : "bg-gray-600 dark:bg-dark-600 text-gray-300 dark:text-dark-300"
                    }`}
                  >
                    OFF
                  </button>
                  <span className="text-[10px] text-gray-500 dark:text-dark-500 ml-1">
                    {debugHolidaySale === null ? "(auto)" : ""}
                  </span>
                </div>
              </div>
            )}
            
            {renderStepContent()}
          </div>
        </div>
      </div>

      {/* Footer - Hidden on step 2 (BuildPlanStep handles its own navigation) */}
      {!isCheckingReinstatement && currentStep !== 2 && (
        <div>
          <div className="max-w-4xl mx-auto px-4 py-2">
            {/* Error Message */}
            {error && (
              <div className="mb-2 p-2 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-xs text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}

            <div
              className={
                "flex items-center justify-between " +
                (currentStep > 1 ? "gap-3" : "")
              }
            >
              {currentStep > 1 && (
                <button
                  onPointerDown={handleBack}
                  className="flex items-center gap-1 px-3 py-2 text-gray-600 dark:text-dark-300 hover:text-gray-800 dark:hover:text-white transition-colors text-xs font-medium"
                >
                  ← Back
                </button>
              )}
              <div className="flex-1" />
              <AuthButton
                onClick={handleContinue}
                disabled={
                  isBillingLoading ||
                  isLoadingProductLink ||
                  (currentStep === 1 && !isZipCode(zipCode))
                }
                className="flex items-center justify-center gap-2 px-2 py-2.5 text-sm"
              >
                {(() => {
                  // Calculate max step based on retailers

                  if (isLastStep && (isBillingLoading || isLoadingProductLink)) {
                    return (
                      <>
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        {isLoadingProductLink ? "Loading payment..." : t("setup.starting")}
                      </>
                    );
                  } else if (isLastStep) {
                    // Check if user is reinstating
                    if (isReinstating) {
                      return "Continue to Payment";
                    }
                    return "Get Started";
                  } else {
                    return (
                      <>
                        {t("setup.continue")}
                        <ChevronRight className="w-4 h-4" />
                      </>
                    );
                  }
                })()}
              </AuthButton>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
